import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SettingsIcon from '@mui/icons-material/Settings'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import PaletteIcon from '@mui/icons-material/Palette'
import InsightsIcon from '@mui/icons-material/Insights'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import axios from 'axios'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import { usePageJob } from '../contexts/pageJob'
import { canPromoteDashboard } from '../utils/roleChecks'
import { canOverwriteDashboard } from '../utils/dashboardOwnership'
import useDashboards from '../hooks/useDashboards'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useDashboardMultiTable from '../hooks/useDashboardMultiTable'
import useBrandPalette from '../hooks/useBrandPalette'
import useBrandThemes from '../hooks/useBrandThemes'
import useClientJobContext from '../hooks/useClientJobContext'
import useDashboardBulkRun, { BulkRunOutcome } from '../hooks/useDashboardBulkRun'
import type { BulkRunTarget } from '../utils/dashboardBulkRun'
import useDashboardInsightsChat from '../hooks/useDashboardInsightsChat'
import useInsightsPersonas from '../hooks/useInsightsPersonas'
import DashboardSettingsModal, { DashboardSettingsTabKey } from '../components/dashboard/DashboardSettingsModal'
import { FilterSignpostProvider, FilterSignpostValue } from '../components/dashboard/FilterSignpost'
import { DashboardJobsProvider, DashboardJobsValue } from '../components/dashboard/DashboardJobs'
import PageBrandColorsPanel from '../components/dashboard/PageBrandColorsPanel'
import CellEditModal from '../components/dashboard/CellEditModal'
import FilterEditorModal from '../components/spec-editor/FilterEditorModal'
import ChatDrawer from '../components/chat/ChatDrawer'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import useSnackbar from '../hooks/useSnackbar'
import { BrandPaletteEntry, DashboardConfig, DashboardLayout, DashboardRow, DashboardCell, DashboardPage, MultiTable, MultiTableConfig, MultiTableAnalysisWidgetData } from '../types/gotypes'
import DashboardEditorRow from '../components/dashboard/editor/DashboardEditorRow'
import { replacePillarEntryCell } from '../components/dashboard/widgets/ReportPillarWidget'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { resolveDashboardLoadState } from '../utils/dashboardLoadState'
import { cardGlobalFilter, cardSendJobFilters, compoundFilters, insightsSourceLines, jobFilterMap, pageFilterForJob, writeJobPageFilter, writePageFilter } from '../utils/dashboardFilters'
import { API_BASE_URL } from '../constants/system'
import { resolvePageColorMap, themeColorMapsFromList, themeLookupFromList, resetChartThemesInLayout } from '../utils/brandTheme'
import { MultiTableRef } from '../utils/dashboardDuplicate'
import { calculateCellWidths, resolveActiveLayout } from '../utils/dashboard'
import {
  MIN_CELL_WIDTH_PCT,
  distributeWidthsEvenly,
  dropTargetRef,
  insertWidgetAtDropTarget,
  moveWidgetToDropTarget,
  redistributeWidths,
  setRowWidths,
} from '../utils/dashboardBuilderLayout'
import WidgetPalette from '../components/dashboard/layout/WidgetPalette'
import LayoutDropZone from '../components/dashboard/layout/LayoutDropZone'
import { WidgetPaletteDragGhost, WidgetDragGhost } from '../components/dashboard/layout/WidgetPaletteCard'
import { LayoutDragData, layoutCollisionDetection, readDragData, readDropTarget, useLayoutDragSensors } from '../components/dashboard/layout/layoutDnd'
import { WIDGET_TYPE_OPTIONS } from '../components/dashboard/widgetTaxonomy'
import useDashboardDuplicate from '../hooks/useDashboardDuplicate'
import useDashboardTemplates from '../hooks/useDashboardTemplates'
import SaveAsTemplateDialog from '../components/dashboard/builder/SaveAsTemplateDialog'
import useDashboardCellSave from '../hooks/useDashboardCellSave'
import { useDisplayNames } from '../utils/displayNames'
import { DashboardGrantContext } from '../contexts/DashboardGrantContext'

/** Collect all unique jobs from table_graph widgets across all pages */
function getTableGraphJobs(config: DashboardConfig): Set<string> {
  const jobs = new Set<string>()
  const scanLayout = (layout: DashboardLayout) => {
    // #1245: rows can be null on an API-created dashboard; the hook
    // normalises on load, this is belt and braces.
    for (const row of layout?.rows ?? []) {
      for (const cell of row.cells ?? []) {
        if (cell.widget_type === 'table_graph' && cell.table_graph_data?.job) {
          jobs.add(cell.table_graph_data.job)
        }
      }
    }
  }
  if (config.pages && config.pages.length > 0) {
    for (const page of config.pages) {
      scanLayout(page.layout)
    }
  } else {
    scanLayout(config.layout)
  }
  return jobs
}

/** Count widget references per job across the whole config (#1162) — mirrors
 *  the server's collectConfigJobs (goapi/pkg/server/dashboard_jobs.go), so the
 *  editor can disable "remove job" for a job still in use before the server
 *  refuses it. Recurses into nested layouts and report pillars. */
function collectConfigJobCounts(config: DashboardConfig): Record<string, number> {
  const counts: Record<string, number> = {}
  const bump = (j?: string) => { if (j) counts[j] = (counts[j] || 0) + 1 }
  const scanCell = (cell: DashboardCell) => {
    if (cell.widget_type === 'table_graph') bump(cell.table_graph_data?.job)
    if (cell.widget_type === 'multi_table_analysis') bump(cell.multi_table_analysis_data?.job)
    if (cell.widget_type === 'image' && cell.image_data?.file_path) bump(cell.image_data?.job)
    if (cell.widget_type === 'video' && cell.video_data?.file_path) bump(cell.video_data?.job)
    if (cell.layout_data) scanLayout(cell.layout_data)
    if (cell.report_pillar_data) {
      for (const entry of cell.report_pillar_data.entries) scanCell(entry.cell)
    }
  }
  const scanLayout = (layout: DashboardLayout) => {
    for (const row of layout?.rows ?? []) for (const cell of row.cells ?? []) scanCell(cell)
  }
  if (config.pages && config.pages.length > 0) for (const p of config.pages) scanLayout(p.layout)
  else scanLayout(config.layout)
  return counts
}

/** Collect multi-table analysis widget data from a layout (recursing into nested layouts) */
function scanLayoutForWidgets(layout: DashboardLayout): MultiTableAnalysisWidgetData[] {
  const widgets: MultiTableAnalysisWidgetData[] = []
  for (const row of layout?.rows ?? []) {
    for (const cell of row.cells ?? []) {
      if (cell.widget_type === 'multi_table_analysis' && cell.multi_table_analysis_data?.multi_table_id) {
        widgets.push(cell.multi_table_analysis_data)
      }
      if (cell.widget_type === 'layout' && cell.layout_data) {
        widgets.push(...scanLayoutForWidgets(cell.layout_data))
      }
      if (cell.widget_type === 'report_pillar' && cell.report_pillar_data) {
        widgets.push(...scanLayoutForWidgets({
          rows: cell.report_pillar_data.entries.map(entry => ({ cells: [entry.cell] })),
        }))
      }
    }
  }
  return widgets
}

/** Return a new config with edited_report cleared on every multi_table_analysis widget
 *  whose multi_table_id is in the targetIds set. */
function clearEditedReports(config: DashboardConfig, targetIds: Set<string>): DashboardConfig {
  const mapLayout = (layout: DashboardLayout): DashboardLayout => ({
    ...layout,
    rows: layout.rows.map(row => ({
      ...row,
      cells: row.cells.map(cell => {
        if (
          cell.widget_type === 'multi_table_analysis'
          && cell.multi_table_analysis_data?.multi_table_id
          && targetIds.has(cell.multi_table_analysis_data.multi_table_id)
        ) {
          return {
            ...cell,
            multi_table_analysis_data: { ...cell.multi_table_analysis_data, edited_report: undefined },
          }
        }
        if (cell.widget_type === 'layout' && cell.layout_data) {
          return { ...cell, layout_data: mapLayout(cell.layout_data) }
        }
        if (cell.widget_type === 'report_pillar' && cell.report_pillar_data) {
          const mapped = mapLayout({
            rows: cell.report_pillar_data.entries.map(entry => ({ cells: [entry.cell] })),
          })
          return {
            ...cell,
            report_pillar_data: {
              ...cell.report_pillar_data,
              entries: cell.report_pillar_data.entries.map((entry, i) => ({ ...entry, cell: mapped.rows[i].cells[0] })),
            },
          }
        }
        return cell
      }),
    })),
  })
  if (config.pages && config.pages.length > 0) {
    return { ...config, pages: config.pages.map(p => ({ ...p, layout: mapLayout(p.layout) })) }
  }
  return { ...config, layout: mapLayout(config.layout) }
}

const DashboardEditor: React.FC = () => {
  const router = useRouter()
  const account = useAccount()

  const snackbar = useSnackbar()
  const customer = router.params.customer || account.customer?.name || ''
  const dashboardId = router.params.dashboardId || ''
  const job = account.job?.name || '' // Active session job — seeds a new dashboard's job at creation

  const {
    dashboard,
    dashboardIsLoading,
    dashboardIsError,
    dashboardError,
    createDashboard,
    updateDashboard,
    updateIsLoading,
    shareDashboard,
    shareIsLoading,
    disableShare,
    forkDashboard,
    forkIsLoading,
    addDashboardJob,
    removeDashboardJob,
    jobMutationIsLoading,
  } = useDashboards({ customer, dashboardId })

  // The dashboard's own persisted job once it exists, falling back to the
  // active session job while creating a new one. This is the single-job
  // boundary (#961): every widget editor below is locked to this job, not to
  // whatever job happens to be active in the session when the dashboard is
  // reopened later.
  const dashboardJob = dashboard?.job || job

  // Publish it to the header's locked Job pill (see contexts/pageJob).
  //
  // NOT `dashboardJob`: that falls back to the session job, which is right for
  // the widget editors (a new dashboard is created against the active job) but
  // wrong for the header. A LOADED dashboard with no job is a legacy pre-#961
  // row, and showing the session job over it states something false — the
  // header would claim a job the dashboard does not have and whose tables it
  // does not reference. Only the create flow, where there is no dashboard yet,
  // may show the session job.
  // A multi-job dashboard publishes its whole set (#1229 C1: pill reads
  // "Multi").
  const { setPageJobs } = usePageJob()
  const headerJobs = dashboard ? (dashboard.jobs?.length ? dashboard.jobs : (dashboard.job ? [dashboard.job] : [])) : (job ? [job] : [])
  const headerJobsKey = headerJobs.join('\u0000')
  useEffect(() => {
    setPageJobs(headerJobs)
    return () => setPageJobs([])
  }, [headerJobsKey])

  // Display names + Context descriptions for Settings → About's Client & Job
  // section (#973 item 7). Display names come from the session; the Context
  // descriptions are the customer/job-level "Context" config (read-only here,
  // edited in Admin → Context).
  const sessionCust = account.user?.session_info?.sessionCusts?.find((c) => c.name === customer)
  const customerDisplayName = sessionCust?.displayName || undefined
  const jobDisplayName =
    sessionCust?.sessionJobs?.find((j) => j.name === dashboardJob)?.displayName || undefined

  // Clone-on-save (#962): anyone in the job may open and edit any dashboard,
  // but only the owner can save in place — plus Managers/Realms on a
  // company-level one. A new dashboard is always the caller's to save.
  const userEmail = account.user?.session_info?.email || ''
  const canOverwrite =
    dashboardId === 'new' ||
    !dashboard ||
    canOverwriteDashboard(dashboard, userEmail, canPromoteDashboard(account.user?.role_permissions))

  // #1262 item 4: a disabled Edit button is not access control. Someone who
  // pastes or bookmarks the editor URL for a dashboard they can't save lands
  // on the viewer instead of an editor that 403s on every keystroke. Fires
  // once the dashboard is known; a change of dashboard re-checks.
  useEffect(() => {
    if (dashboardId !== 'new' && dashboard && !canOverwrite) {
      router.navigate('dashboard-view', { customer, dashboardId })
    }
  }, [dashboard?.id, canOverwrite])

  // Hook for managing dashboard-owned multi-tables (cross-job support)
  const dashboardMultiTable = useDashboardMultiTable({
    customer,
    dashboardId: dashboardId !== 'new' ? dashboardId : '',
  })

  const [config, setConfig] = useState<DashboardConfig>({
    name: '',
    description: '',
    layout: { rows: [] },
    pages: [{ id: crypto.randomUUID(), name: 'Page 1', layout: { rows: [] } }],
  })

  const [settingsOpen, setSettingsOpen] = useState(false)
  // The page helper (Douglas plan T15). It shares the right edge with the
  // Insights pane, so opening one closes the other.
  const [helperOpen, setHelperOpen] = useState(false)
  const [settingsTab, setSettingsTab] = useState<DashboardSettingsTabKey>('about')
  // "Save as template" (#1179) — moved here from the DataViz homepage row
  // actions, reachable via the settings modal.
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  // Which page's "Page brand colors" panel is open (#1182) — page index, or
  // null when closed.
  const [paletteTargetIndex, setPaletteTargetIndex] = useState<number | null>(null)
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; cellIndex: number; innerRowIndex?: number; innerCellIndex?: number } | null>(null)
  const [activePageIndex, setActivePageIndex] = useState(0)
  const [renamingPageIndex, setRenamingPageIndex] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [pageMenuAnchor, setPageMenuAnchor] = useState<HTMLElement | null>(null)
  const [pageMenuIndex, setPageMenuIndex] = useState<number | null>(null)
  const [deletePageIndex, setDeletePageIndex] = useState<number | null>(null)
  // Palette overlay state — persisted per browser, never on the dashboard.
  const [paletteCollapsed, setPaletteCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('dashboardEditorPaletteCollapsed') === '1' } catch { return false }
  })
  const handleTogglePalette = () => {
    setPaletteCollapsed(prev => {
      try { localStorage.setItem('dashboardEditorPaletteCollapsed', prev ? '0' : '1') } catch { /* private mode */ }
      return !prev
    })
  }
  // The page-filter dialog (#1183; #1229 A1: the ONLY filter authoring —
  // "Apply to all pages" is gone). On a multi-job dashboard `filterJob` is
  // the job a chip click preselects inside the dialog (#1229 C4).
  const [pageFilterOpen, setPageFilterOpen] = useState(false)
  const [filterJob, setFilterJob] = useState('')
  const [regenMenuAnchor, setRegenMenuAnchor] = useState<HTMLElement | null>(null)

  // Insights pane (#974) — persistent, opens fresh each time, mirrors the
  // Tables Insights pane one level up in scope (whole dashboard, not one table).
  // Bound to the page the editor is on when the pane opens, plus the
  // dashboard's saved global filter (#974 Stage 1, #1064). `pages`/`activePage`
  // are re-derived further down for rendering; this read is the same value,
  // needed here because the hook owns the binding.
  const insightsPage = (config.pages || [])[activePageIndex] || (config.pages || [])[0]
  // #1240 B1: per-job framing for the pane (no quick filter in edit mode).
  // `dashboardJobs`/`jobLabel` are derived further down for rendering; this
  // is the same computation, needed here because the hook owns the binding.
  const insightsJobs = dashboard?.jobs?.length ? dashboard.jobs : (dashboardJob ? [dashboardJob] : [])
  const insightsStoredFilters = jobFilterMap(config, insightsPage, insightsJobs, insightsJobs[0] || '')
  const insightsSources = insightsJobs.map((j) => ({
    job: j,
    display_name: sessionCust?.sessionJobs?.find((sj) => sj.name === j)?.displayName || j,
    page_filter: insightsStoredFilters[j] || '',
  }))
  const insights = useDashboardInsightsChat({
    customer,
    dashboardId,
    pageId: insightsPage?.id || '',
    pageName: insightsPage?.name || '',
    // #1183: insights read the same resolved (dashboard AND page) filter
    // the page's widgets run under — through the per-job map, like the
    // viewer, so a page whose filter lives in the map is not read as empty.
    filter: insightsJobs.length
      ? (insightsStoredFilters[insightsJobs[0]] || '')
      : compoundFilters(config.global_filter, insightsPage?.filter),
    sources: insightsSources,
  })
  // Persona list for the Insights pane (#974 ask 1) — same source as the
  // Tables pane: defaults + custom personas from the job-level merged config.
  const insightsPersonas = useInsightsPersonas(customer)
  // AI access (#1537): the insights pane and regenerating Gen AI cards need
  // level 2. Below it the controls are greyed; the server refuses too.
  const aiAccess = useAiAccess(customer)
  // Who to ask about a theme the user can't change (the page palette's theme manager).
  const whoToAsk = useWhoToAsk(customer)
  const canInsights = aiAccess.can('dashboard_insights')
  const canRegenerate = aiAccess.can('genai_cards')

  // Drag-and-drop layout (#971 / #967 / #1049): a widget dragged out of the
  // side palette or off the canvas, and the drop it is currently over. Both
  // shapes are the ones the template builder uses — see layoutDnd.ts.
  const [activeDrag, setActiveDrag] = useState<LayoutDragData | null>(null)
  const dragSensors = useLayoutDragSensors()

  const brandPalette = useBrandPalette(customer, dashboardJob)
  const brandThemes = useBrandThemes(customer)
  // Only fetched while the settings modal is open — the About tab is the sole
  // consumer.
  const clientJobContext = useClientJobContext(customer, dashboardJob, settingsOpen)
  const { createTemplate, createTemplateIsLoading } = useDashboardTemplates({ customer })

  // Same semantics as the old homepage action: the saved template is always
  // personal (owned by the caller); company promotion stays a separate,
  // explicit Manager action in the template library.
  const handleSaveAsTemplate = async (templateName: string, tags: string[]) => {
    if (dashboardId === 'new') return
    const created = await createTemplate({ sourceDashboardId: dashboardId, name: templateName, tags })
    if (created) {
      snackbar.success(`Saved "${created.config?.name || templateName}" as a personal template`)
      setTemplateDialogOpen(false)
    }
  }

  // Debounce timer ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Track pending config for flush-before-navigate
  const pendingConfigRef = useRef<DashboardConfig | null>(null)
  // Track which dashboardId we've loaded config for — only load once per dashboard
  // to prevent the invalidate→refetch→useEffect cycle from resetting local edits
  const configLoadedForRef = useRef('')
  // Latest config for async handlers that resume after an await (a regenerate
  // run or a paste's multi-table fork can take a while, and the user may keep
  // editing meanwhile). Every commit path writes through these refs so an async
  // handler never rebuilds the config from the render closure it started in.
  const configRef = useRef(config)
  configRef.current = config
  const activePageIndexRef = useRef(activePageIndex)
  activePageIndexRef.current = activePageIndex

  // Load dashboard config when dashboard data is available (once per dashboardId)
  React.useEffect(() => {
    if (dashboard?.config && configLoadedForRef.current !== dashboardId) {
      configLoadedForRef.current = dashboardId
      // Ensure pages array exists (migrate legacy single-layout dashboards)
      const cfg = { ...dashboard.config }
      if (!cfg.pages || cfg.pages.length === 0) {
        cfg.pages = [{ id: crypto.randomUUID(), name: 'Page 1', layout: cfg.layout || { rows: [] } }]
      }
      setConfig(cfg)
    }
  }, [dashboard, dashboardId])

  // For new dashboards, open settings modal immediately
  React.useEffect(() => {
    if (dashboardId === 'new' && !dashboard) {
      setSettingsOpen(true)
    }
  }, [dashboardId, dashboard])

  // Optimistic-concurrency precondition (#963). Held in a ref so the debounced
  // save always sends the version this editor last saw without re-creating the
  // callback (which would reset the debounce timer on every refetch).
  const lastSeenUpdatedAtRef = useRef<number | undefined>(undefined)
  lastSeenUpdatedAtRef.current = dashboard?.updated_at

  // Same trick for the overwrite right: read by the debounced save without
  // re-creating the callback (which would reset the debounce timer).
  const canOverwriteRef = useRef(true)
  canOverwriteRef.current = canOverwrite

  // Save implementation (shared by debounced save and flush)
  const saveConfig = useCallback(
    async (newConfig: DashboardConfig) => {
      try {
        if (dashboardId === 'new') {
          if (!newConfig.name) return
          if (!job) {
            snackbar.error('Open a job in your session before creating a dashboard.')
            return
          }
          const createdDashboard = await createDashboard(newConfig, job)
          if (createdDashboard) {
            router.navigate('dashboard-editor', {
              customer,
              dashboardId: createdDashboard.id,
            })
          }
        } else if (canOverwriteRef.current) {
          await updateDashboard(dashboardId, newConfig, lastSeenUpdatedAtRef.current)
        }
        // Not the owner: the edits stay in local state until the user takes
        // them with "Save to My dashboards". Auto-saving here would post a
        // 403 on every debounce tick and toast on each one.
      } catch (error) {
        console.error('[Dashboard] Auto-save failed:', error)
      }
      pendingConfigRef.current = null
    },
    [dashboardId, customer, job, createDashboard, updateDashboard, router, snackbar]
  )

  // "Save to My dashboards" (#962) — forks the CURRENT on-screen config, not
  // the stored one, so the user keeps the edits they just made, and lands in
  // the copy they now own.
  const handleSaveToMyDashboards = async () => {
    if (dashboardId === 'new') return
    const forked = await forkDashboard(dashboardId, configRef.current)
    if (forked) {
      snackbar.success(`Saved to My dashboards as "${forked.config?.name || forked.id}"`)
      router.navigate('dashboard-editor', { customer, dashboardId: forked.id })
    }
  }

  // Debounced save function
  const debouncedSave = useCallback(
    (newConfig: DashboardConfig) => {
      pendingConfigRef.current = newConfig
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
      saveTimeoutRef.current = setTimeout(() => saveConfig(newConfig), 1000)
    },
    [saveConfig]
  )

  // Flush any pending save immediately (used before navigation)
  const flushSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    if (pendingConfigRef.current) {
      await saveConfig(pendingConfigRef.current)
    }
  }, [saveConfig])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // The single commit path for a config change. Keeps `configRef` ahead of the
  // next render so a handler that commits and then awaits (or two commits in
  // one tick) still builds on what was just committed.
  const handleConfigChange = (newConfig: DashboardConfig) => {
    configRef.current = newConfig
    setConfig(newConfig)
    // Sync root layout from active page for backwards compatibility
    const pages = newConfig.pages || []
    const activePage = pages[activePageIndexRef.current]
    const syncedConfig = activePage
      ? { ...newConfig, layout: activePage.layout }
      : newConfig
    debouncedSave(syncedConfig)
  }

  // Get the active page's layout (or fallback to root layout)
  const pages = config.pages || []
  const activePage = pages[activePageIndex] || pages[0]
  const activeLayout = resolveActiveLayout(config, activePageIndex)

  // Check if analysis widgets exist (for enabling run/cache buttons)
  const pageHasAnalysisWidgets = scanLayoutForWidgets(activeLayout).length > 0
  const anyPageHasAnalysisWidgets = (config.pages || []).some(p => scanLayoutForWidgets(p.layout).length > 0)
    || scanLayoutForWidgets(config.layout || { rows: [] }).length > 0

  // multi_table_id -> widget title, so Settings -> History -> Sign-offs can
  // name cards the way the dashboard does (#974), same as DashboardView.
  const widgetTitles: Record<string, string> = {}
  for (const w of [
    ...(config.pages || []).flatMap(p => scanLayoutForWidgets(p.layout)),
    ...scanLayoutForWidgets(config.layout || { rows: [] }),
  ]) {
    if (w.multi_table_id && w.title) widgetTitles[w.multi_table_id] = w.title
  }

  // Commits a layout onto a page. Reads the config from a ref, not the render
  // closure, so an async caller (paste, cell save) that resumes after an await
  // does not overwrite edits the user made while it waited.
  //
  // `pageId` names the destination. An async caller captures it before its
  // awaits and passes it here, because the active page index is not the page it
  // was aimed at any more once the user switches tab or reorders pages
  // mid-await. Synchronous callers omit it and get the active page.
  const handleLayoutChange = (layout: DashboardLayout, pageId?: string) => {
    const current = configRef.current
    if (!current.pages || current.pages.length === 0) {
      handleConfigChange({ ...current, layout })
      return
    }
    const targetIndex = pageId === undefined
      ? activePageIndexRef.current
      : current.pages.findIndex(p => p.id === pageId)
    // The page went away while the caller was awaiting. Callers check for this
    // and report it; committing here would write onto a different page.
    if (targetIndex < 0) return
    const newPages = current.pages.map((p, i) =>
      i === targetIndex ? { ...p, layout } : p
    )
    handleConfigChange({ ...current, pages: newPages })
  }

  // Page CRUD operations
  const handleAddPage = () => {
    // #1182: a new page inherits the palette of the page to its left — new
    // pages append, so that is the current last page.
    const existing = config.pages || []
    const inheritedKey = existing[existing.length - 1]?.brand_palette_key
    const newPage: DashboardPage = {
      id: crypto.randomUUID(),
      name: `Page ${(config.pages?.length || 0) + 1}`,
      layout: { rows: [] },
      ...(inheritedKey ? { brand_palette_key: inheritedKey } : {}),
    }
    const newPages = [...(config.pages || []), newPage]
    const newConfig = { ...config, pages: newPages }
    handleConfigChange(newConfig)
    setActivePageIndex(newPages.length - 1)
  }

  const handleDeletePage = (pageIndex: number) => {
    const newPages = (config.pages || []).filter((_, i) => i !== pageIndex)
    if (newPages.length === 0) return // Don't delete last page
    handleConfigChange({ ...config, pages: newPages })
    if (activePageIndex >= newPages.length) {
      setActivePageIndex(newPages.length - 1)
    }
  }

  const handleRenamePage = (pageIndex: number, newName: string) => {
    if (!newName.trim()) return
    const newPages = (config.pages || []).map((p, i) =>
      i === pageIndex ? { ...p, name: newName.trim() } : p
    )
    handleConfigChange({ ...config, pages: newPages })
    setRenamingPageIndex(null)
  }

  const handleMovePageLeft = (pageIndex: number) => {
    if (pageIndex <= 0) return
    const newPages = [...(config.pages || [])]
    ;[newPages[pageIndex - 1], newPages[pageIndex]] = [newPages[pageIndex], newPages[pageIndex - 1]]
    handleConfigChange({ ...config, pages: newPages })
    if (activePageIndex === pageIndex) setActivePageIndex(pageIndex - 1)
    else if (activePageIndex === pageIndex - 1) setActivePageIndex(pageIndex)
  }

  const handleMovePageRight = (pageIndex: number) => {
    const p = config.pages || []
    if (pageIndex >= p.length - 1) return
    const newPages = [...p]
    ;[newPages[pageIndex], newPages[pageIndex + 1]] = [newPages[pageIndex + 1], newPages[pageIndex]]
    handleConfigChange({ ...config, pages: newPages })
    if (activePageIndex === pageIndex) setActivePageIndex(pageIndex + 1)
    else if (activePageIndex === pageIndex + 1) setActivePageIndex(pageIndex)
  }

  // Per-PAGE colours (#1182): the active page's theme (a live reference by
  // id) merged over the job palette; the legacy config snapshot only backs up
  // an absent job palette. Editing a theme recolours here on the next themes
  // fetch — no reassignment.
  const colorMap = useMemo(() => {
    const base = brandPalette.palette || config.brand_palette
    return resolvePageColorMap(activePage, themeLookupFromList(brandThemes.themes), base)
  }, [brandPalette.palette, config.brand_palette, activePage, brandThemes.themes])

  // Chart-level themes (#1193): every theme flattened by id, so a widget's
  // own brand_palette_key resolves inside the renderer.
  const themeColorMaps = useMemo(() => themeColorMapsFromList(brandThemes.themes), [brandThemes.themes])
  const pageThemeName = activePage?.brand_palette_key
    ? brandThemes.themes.find((t) => t.id === activePage.brand_palette_key)?.name
    : undefined

  // The page theme is a live reference: store the id, never the colours.
  const handleSelectPageTheme = (pageIndex: number, themeId: string | undefined) => {
    const current = configRef.current
    const newPages = (current.pages || []).map((p, i) =>
      i === pageIndex ? { ...p, brand_palette_key: themeId } : p
    )
    handleConfigChange({ ...current, pages: newPages })
  }

  // Manager-only: write a theme's palette onto the job config — the job
  // palette's only editor now the Appearance tab is gone (#1182). Also
  // snapshots into the dashboard config, which is what a shared viewer's
  // fallback layer reads.
  // "Reset all charts on page" (#1175, Matt 1 Sep — replaces "Set as job
  // default"): strips every chart-level theme reference on the page so its
  // charts follow the page theme again. Returns the count for the panel's
  // button feedback.
  const handleResetPageCharts = (pageIndex: number): number => {
    const current = configRef.current
    const page = current.pages?.[pageIndex]
    if (!page) return 0
    const { layout, count } = resetChartThemesInLayout(page.layout)
    if (count === 0) return 0
    const pages = current.pages!.map((p, i) => (i === pageIndex ? { ...p, layout: layout! } : p))
    handleConfigChange({ ...current, pages })
    return count
  }

  // Run analysis
  const [overwriteConfirm, setOverwriteConfirm] = useState<{
    targets: BulkRunTarget[]
    edited: MultiTableAnalysisWidgetData[]
    label: string
  } | null>(null)

  // Per-card status for the most recent "Regenerate AI Insights" run — this is
  // what lets the toolbar surface queued/running/failed cards instead of the
  // user having to hunt for stale ones after a bulk regenerate. All the
  // networking (cache warm, start, progress polling) lives in the hook.
  const bulkRun = useDashboardBulkRun({ customer, job: dashboardJob, dashboardId })

  /**
   * #1810: the filter map a Gen AI card on `page` sends when it starts — the
   * same map the card itself compares its analysis's snapshot with: the
   * page's per-job map on a multi-job dashboard, else the resolved page
   * filter for the card's own job. Read from the ref, so a handler that
   * runs after an await still sees the latest config.
   */
  const cardRunFilters = (page: DashboardPage | undefined, widgetJob: string | undefined): Record<string, string> => {
    const cfg = configRef.current
    return cardSendJobFilters(
      widgetJob || dashboardJob,
      isMultiJob ? jobFilterMap(cfg, page, dashboardJobs, primaryJob) : undefined,
      cardGlobalFilter(widgetJob, dashboardJob, undefined, compoundFilters(cfg.global_filter, pageFilterForJob(page, primaryJob, primaryJob))),
    )
  }
  const runTargetsFor = (page: DashboardPage | undefined, layout: DashboardLayout | undefined): BulkRunTarget[] =>
    scanLayoutForWidgets(layout || { rows: [] }).map(widget => ({ widget, jobFilters: cardRunFilters(page, widget.job) }))

  const requestRun = (targets: BulkRunTarget[], label: string) => {
    const widgets = targets.map(t => t.widget)
    // #1219 gate 2: Run Page / Run All spend tokens regenerating every Gen AI
    // card on the page/dashboard — the same overwrite authority that gates
    // the per-card ↻ button. In practice #1262 already redirects a
    // non-overwriter out of the editor before this is reachable; this is
    // belt-and-braces against a stale voice-tool closure or a permission
    // that changed mid-session.
    if (!canOverwrite) {
      snackbar.warning('You do not have permission to regenerate this dashboard.')
      return
    }
    if (!canRegenerate) {
      snackbar.warning(aiAccess.refusalFor('genai_cards'))
      return
    }
    if (bulkRun.isBusy) {
      snackbar.warning('A regenerate run is still in progress')
      return
    }
    if (widgets.length === 0) {
      snackbar.warning('No analysis widgets found')
      return
    }
    // Always confirm: the dialog states how the report months are derived
    // (and warns about manual edits when any would be overwritten).
    const edited = widgets.filter(w => w.edited_report && w.edited_report.length > 0)
    setOverwriteConfirm({ targets, edited, label })
  }

  const handleRunPage = () => {
    requestRun(runTargetsFor(activePage, activeLayout), 'This page')
  }

  // Run All spans pages: each card runs under its own page's filters (#1810).
  const handleRunAll = () => {
    const allTargets = (config.pages || []).flatMap(p => runTargetsFor(p, p.layout))
    requestRun(allTargets.length > 0 ? allTargets : runTargetsFor(activePage, config.layout), 'Whole dashboard')
  }

  const reportRunOutcome = (label: string, outcome: BulkRunOutcome) => {
    // #1219 gate 2: a regenerate refusal is reported apart from a failure —
    // "n not permitted" is a permission decision, not a bug, and it is never
    // folded into "n failed" (which "Retry failed" would then wrongly offer).
    if (outcome.failedCount === 0 && outcome.refusedCount === 0) {
      snackbar.success(`${label}: started ${outcome.startedCount} analyse${outcome.startedCount !== 1 ? 's' : ''}`)
      return
    }
    const total = outcome.startedCount + outcome.failedCount + outcome.refusedCount
    const settledParts = [
      outcome.failedCount > 0 ? `failed ${outcome.failedCount}` : null,
      outcome.refusedCount > 0 ? `not permitted ${outcome.refusedCount}` : null,
    ].filter(Boolean).join(', ')
    snackbar.warning(`${label}: started ${outcome.startedCount}, ${settledParts} of ${total}`)
  }

  const handleRetryFailedBulkRun = () => {
    const label = bulkRun.run?.label || 'Retry'
    void bulkRun.retryFailed().then(outcome => {
      if (outcome) reportRunOutcome(label, outcome)
    })
  }

  const handleConfirmOverwrite = async () => {
    if (!overwriteConfirm) return
    const { targets, edited, label } = overwriteConfirm
    setOverwriteConfirm(null)

    const outcome = await bulkRun.startRun(targets, label)
    if (!outcome) return
    reportRunOutcome(label, outcome)

    // Only clear manual edits for widgets that actually queued a NEW analysis.
    // A start that 409s against an already-running analysis queues nothing, so
    // wiping edited_report there would destroy the text with no replacement
    // coming.
    const freshIds = new Set(outcome.freshWidgets.map(w => w.multi_table_id).filter(Boolean) as string[])
    const editedFresh = edited.filter(w => w.multi_table_id && freshIds.has(w.multi_table_id))
    if (editedFresh.length === 0) return

    // Best-effort: log each overwrite to the dashboard edit history
    if (dashboardId) {
      await Promise.allSettled(
        editedFresh.map(w =>
          axios.post(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/edit-history`, {
            widget_title: w.title || '',
            widget_section: w.multi_table_id || '',
            edit_type: 'report_edit',
            before_text: w.edited_report || '',
            after_text: '',
          })
        )
      )
    }

    // Clear edited_report on the regenerated widgets in local config state and
    // persist immediately so the change survives a reload even if the user
    // bails out of the editor without an explicit save.
    // configRef, not just setConfig: this commit is followed by an await, and
    // anything that resumes during it (a paste's fork, a cell save) rebuilds
    // from the ref. Leaving the ref behind here would resurrect the edits.
    const newConfig = clearEditedReports(configRef.current, freshIds)
    configRef.current = newConfig
    setConfig(newConfig)
    if (dashboardId && dashboardId !== 'new') {
      try {
        await updateDashboard(dashboardId, newConfig, lastSeenUpdatedAtRef.current)
      } catch (err) {
        snackbar.warning('Failed to persist cleared edits — they may reappear on reload')
      }
    }
  }

  // Global filter
  const tableGraphJobs = getTableGraphJobs(config)
  // #1162: the dashboard's scope is its recorded job set; the primary job
  // anchors legacy single-job filter authoring.
  const dashboardJobs = dashboard?.jobs?.length
    ? dashboard.jobs
    : dashboardJob
      ? [dashboardJob]
      : [...tableGraphJobs]
  const primaryJob = dashboardJobs[0] || ''
  const isMultiJob = dashboardJobs.length > 1
  const isSingleJob = tableGraphJobs.size === 1
  const hasTableWidgets = tableGraphJobs.size > 0
  const filterDisabledReason = !hasTableWidgets
    ? 'Add table widgets to enable filtering'
    : (!isMultiJob && !isSingleJob)
      ? 'Filtering is only available when all tables are from the same job'
      : undefined
  // On a multi-job dashboard the preview resolves each widget's filter from
  // the per-job maps.
  const jobFilters = isMultiJob
    ? jobFilterMap(config, activePage, dashboardJobs, primaryJob)
    : undefined

  // Per-job page filter authoring (#1162): the map is written per job.
  // #1240 A2: writeJobPageFilter folds a converted page's legacy string
  // into the primary entry first, so a cleared filter stays cleared.
  const setJobPageFilter = (jobId: string, expr: string) => {
    const current = configRef.current
    const idx = activePageIndexRef.current
    const newPages = (current.pages || []).map((p, i) =>
      i === idx ? writeJobPageFilter(p, primaryJob, jobId, expr) : p
    )
    handleConfigChange({ ...current, pages: newPages })
  }

  // Page filter (#1183). Always compounds with the dashboard filter —
  // resolved ONCE per page render here, not per widget. Read through the
  // per-job map when the page has one (a dashboard that collapsed from
  // multi-job to one job), the same home the viewer resolves from.
  const singleJobPageFilter = pageFilterForJob(activePage, primaryJob, primaryJob)
  const resolvedPageFilter = compoundFilters(config.global_filter, singleJobPageFilter)

  // Always the DISPLAY name in the UI (Matt, 3 Sep).
  const jobLabel = (j: string) => sessionCust?.sessionJobs?.find((sj) => sj.name === j)?.displayName || j
  const dashboardJobsValue: DashboardJobsValue = {
    jobs: dashboardJobs.map((j) => ({ name: j, displayName: jobLabel(j) })),
    primaryJob,
  }
  // Widget signposting in edit mode (#1229 C6): job display name + stored
  // page filter. No quick filter here — that is the viewer's.
  const editorStoredFilters = jobFilterMap(config, activePage, dashboardJobs, primaryJob)
  const signpostValue: FilterSignpostValue = {
    multiJob: isMultiJob,
    primaryJob,
    byJob: Object.fromEntries(dashboardJobs.map((j) => [j, {
      jobLabel: jobLabel(j),
      pageFilter: editorStoredFilters[j] || '',
      quickFilter: '',
    }])),
  }

  const handlePageFilterSave = (filterString: string) => {
    const current = configRef.current
    const newPages = (current.pages || []).map((p, i) =>
      i === activePageIndexRef.current ? writePageFilter(p, primaryJob, filterString) : p
    )
    handleConfigChange({ ...current, pages: newPages })
    setPageFilterOpen(false)
  }

  const handleClearPageFilter = () => {
    handlePageFilterSave('')
  }

  const handleSettingsSave = (updates: { name: string; description: string; objectives: string; keyMetrics: string; pages: DashboardPage[] }) => {
    // The modal snapshots config.pages at open time, so a voice add_page/
    // add_row (or any other config change) made while it's open must not be
    // clobbered on Save. Merge only the field the modal actually edits
    // (per-page `context`) onto the current, live config.pages by id —
    // never replace the array wholesale.
    const contextByPageId = new Map(updates.pages.map((p) => [p.id, p.context]))
    const mergedPages = config.pages?.map((p) =>
      contextByPageId.has(p.id) ? { ...p, context: contextByPageId.get(p.id) } : p
    )
    const newConfig = {
      ...config,
      name: updates.name,
      description: updates.description,
      objectives: updates.objectives,
      key_metrics: updates.keyMetrics,
      ...(mergedPages && mergedPages.length > 0 ? { pages: mergedPages } : {}),
    }
    handleConfigChange(newConfig)
    setSettingsOpen(false)
  }

  const openSettings = (tab: DashboardSettingsTabKey) => {
    setSettingsTab(tab)
    setSettingsOpen(true)
  }

  const handleBackClick = async () => {
    await flushSave()
    router.navigate('dashboards', { customer })
  }

  const handleViewClick = async () => {
    if (dashboardId !== 'new') {
      await flushSave()
      router.navigate('dashboard-view', { customer, dashboardId })
    }
  }

  // --- Layout: drag-and-drop placement (#971 Obs B, #967, #1015) ---
  //
  // There is no "add a row then pick a widget" path any more, and no "+ Add
  // cell" that silently did nothing at the cap. Structure emerges from where a
  // widget is dropped: a drop at a seam between rows makes a row; a drop
  // between two widgets makes a column in that row; a drop into a full row
  // spills into a new row below rather than being refused. All of that lives in
  // the pure functions the template builder already uses.

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDrag(readDragData(event.active.data.current) || null)
  }

  const handleDragCancel = () => setActiveDrag(null)

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null)
    const { active, over } = event
    if (!over) return
    const source = readDragData(active.data.current)
    const target = readDropTarget(over.data.current)
    if (!source || !target) return

    if (source.source === 'palette') {
      // Where the placeholder lands, computed BEFORE the insert — a drop into a
      // full row spills into a new row, so the requested index is not where the
      // widget ends up. The user gets its editor straight away, which is the
      // step the old insert-then-pick flow made them do first.
      const landed = dropTargetRef(activeLayout, target)
      handleLayoutChange(insertWidgetAtDropTarget(activeLayout, target, source.widgetType, dashboardJob))
      // #1187: a spacer has nothing to configure, so don't bounce the user
      // into an editor that would only explain itself.
      if (source.widgetType !== 'spacer') setEditingCell(landed)
      return
    }
    handleLayoutChange(moveWidgetToDropTarget(activeLayout, source.ref, target))
  }

  /**
   * Append a row holding one empty text widget, and open its editor.
   *
   * There is no button for this any more — on screen a row appears because a
   * widget was dropped at a seam. It survives for the voice assistant's
   * `add_row` action, which has no pointer to drag with.
   */
  const handleAddRow = () => {
    const newRow: DashboardRow = { cells: [{ widget_type: 'text', text_data: { content: '' } }] }
    handleLayoutChange({ ...activeLayout, rows: [...activeLayout.rows, newRow] })
    setEditingCell({ rowIndex: activeLayout.rows.length, cellIndex: 0 })
  }

  // Row operations
  const handleDeleteRow = (rowIndex: number) => {
    const newRows = activeLayout.rows.filter((_, i) => i !== rowIndex)
    handleLayoutChange({ ...activeLayout, rows: newRows })
  }

  /** Boundary drag (#1049) — width moves between two neighbours, nothing else. */
  const handleResizeBoundary = (rowIndex: number, boundaryIndex: number, deltaPct: number) => {
    const row = activeLayout.rows[rowIndex]
    if (!row) return
    const widths = redistributeWidths(calculateCellWidths(row.cells), boundaryIndex, deltaPct, MIN_CELL_WIDTH_PCT)
    handleLayoutChange(setRowWidths(activeLayout, rowIndex, widths))
  }

  const handleDistributeEvenly = (rowIndex: number) => {
    const row = activeLayout.rows[rowIndex]
    if (!row) return
    handleLayoutChange(setRowWidths(activeLayout, rowIndex, distributeWidthsEvenly(row.cells.length)))
  }

  const handleDeleteCell = (rowIndex: number, cellIndex: number) => {
    const newRows = [...activeLayout.rows]
    const newCells = newRows[rowIndex].cells.filter((_, i) => i !== cellIndex)

    // If row becomes empty, delete the row
    if (newCells.length === 0) {
      handleDeleteRow(rowIndex)
    } else {
      newRows[rowIndex] = { ...newRows[rowIndex], cells: newCells }
      handleLayoutChange({ ...activeLayout, rows: newRows })
    }
  }

  const handleEditCell = (rowIndex: number, cellIndex: number) => {
    setEditingCell({ rowIndex, cellIndex })
  }

  // "Remove reference" on a broken table widget (#969 Part E) — drops the
  // table_path/spec so the widget falls back to its empty state, where
  // Select Table / Create New are already offered. Only the reference goes;
  // view config (title, design overrides) is dropped with it since none of
  // it is meaningful pointed at nothing, matching what picking a fresh table
  // in Edit Data already does (see TableGraphWidget.tsx's handleEditDataSaved).
  const handleRemoveTableReference = (rowIndex: number, cellIndex: number) => {
    const cell = activeLayout.rows[rowIndex]?.cells[cellIndex]
    if (!cell?.table_graph_data) return
    const newRows = [...activeLayout.rows]
    const newCells = [...newRows[rowIndex].cells]
    newCells[cellIndex] = {
      ...cell,
      table_graph_data: { job: cell.table_graph_data.job, table_path: '', view_mode: cell.table_graph_data.view_mode || 'table' },
    }
    newRows[rowIndex] = { ...newRows[rowIndex], cells: newCells }
    handleLayoutChange({ ...activeLayout, rows: newRows })
  }

  const handleMoveRowUp = (rowIndex: number) => {
    if (rowIndex === 0) return
    const newRows = [...activeLayout.rows]
    ;[newRows[rowIndex - 1], newRows[rowIndex]] = [newRows[rowIndex], newRows[rowIndex - 1]]
    handleLayoutChange({ ...activeLayout, rows: newRows })
  }

  const handleMoveRowDown = (rowIndex: number) => {
    if (rowIndex >= activeLayout.rows.length - 1) return
    const newRows = [...activeLayout.rows]
    ;[newRows[rowIndex], newRows[rowIndex + 1]] = [newRows[rowIndex + 1], newRows[rowIndex]]
    handleLayoutChange({ ...activeLayout, rows: newRows })
  }

  // --- Nested layout operations (for editing rows inside layout-type cells) ---

  const updateNestedLayout = (rowIndex: number, cellIndex: number, updater: (rows: DashboardRow[]) => DashboardRow[]) => {
    // Read the layout live rather than from the render closure — same reason as
    // handleLayoutChange above.
    const layout = resolveActiveLayout(configRef.current, activePageIndexRef.current)
    const outerCell = layout.rows[rowIndex]?.cells[cellIndex]
    if (!outerCell?.layout_data) return
    const newInnerRows = updater([...outerCell.layout_data.rows])
    const updatedCell = { ...outerCell, layout_data: { ...outerCell.layout_data, rows: newInnerRows } }
    const newRows = layout.rows.map((row, i) => {
      if (i !== rowIndex) return row
      return { ...row, cells: row.cells.map((c, j) => (j === cellIndex ? updatedCell : c)) }
    })
    handleLayoutChange({ ...layout, rows: newRows })
  }

  const handleInsertInnerRow = (rowIndex: number, cellIndex: number, afterInnerRowIndex: number) => {
    const newRow: DashboardRow = { cells: [{ widget_type: 'text', text_data: { content: '' } }] }
    updateNestedLayout(rowIndex, cellIndex, (rows) => {
      rows.splice(afterInnerRowIndex + 1, 0, newRow)
      return rows
    })
    setEditingCell({ rowIndex, cellIndex, innerRowIndex: afterInnerRowIndex + 1, innerCellIndex: 0 })
  }

  const handleEditInnerCell = (rowIndex: number, cellIndex: number, innerRowIndex: number, innerCellIndex: number) => {
    setEditingCell({ rowIndex, cellIndex, innerRowIndex, innerCellIndex })
  }

  const handleDeleteInnerRow = (rowIndex: number, cellIndex: number, innerRowIndex: number) => {
    updateNestedLayout(rowIndex, cellIndex, (rows) => {
      rows.splice(innerRowIndex, 1)
      return rows
    })
  }

  const handleMoveInnerRow = (rowIndex: number, cellIndex: number, innerRowIndex: number, direction: 'up' | 'down') => {
    updateNestedLayout(rowIndex, cellIndex, (rows) => {
      const targetIndex = direction === 'up' ? innerRowIndex - 1 : innerRowIndex + 1
      if (targetIndex < 0 || targetIndex >= rows.length) return rows
      const temp = rows[innerRowIndex]
      rows[innerRowIndex] = rows[targetIndex]
      rows[targetIndex] = temp
      return rows
    })
  }

  const handleUpdateInnerCellData = (rowIndex: number, cellIndex: number, innerRowIndex: number, innerCellIndex: number, updatedCell: DashboardCell) => {
    // Report pillars (#1080) hold their cards as entries, not layout rows —
    // entry k addresses as inner row k, cell 0 (#1104). updateNestedLayout
    // requires layout_data and would silently no-op on a pillar, which is how
    // Regenerate on a pillar card in the editor ran the analysis but never
    // persisted awaiting_review — so a stored human edit kept masking the
    // fresh text and the card looked like nothing happened (2026-08-25).
    const layout = resolveActiveLayout(configRef.current, activePageIndexRef.current)
    const outerCell = layout.rows[rowIndex]?.cells[cellIndex]
    if (outerCell?.widget_type === 'report_pillar' && outerCell.report_pillar_data) {
      const updatedOuter: DashboardCell = {
        ...outerCell,
        report_pillar_data: replacePillarEntryCell(outerCell.report_pillar_data, innerRowIndex, updatedCell),
      }
      handleLayoutChange({
        ...layout,
        rows: layout.rows.map((row, i) => (
          i === rowIndex ? { ...row, cells: row.cells.map((c, j) => (j === cellIndex ? updatedOuter : c)) } : row
        )),
      })
      return
    }
    updateNestedLayout(rowIndex, cellIndex, (rows) => {
      return rows.map((r, ri) => {
        if (ri !== innerRowIndex) return r
        return { ...r, cells: r.cells.map((c, ci) => ci === innerCellIndex ? updatedCell : c) }
      })
    })
  }

  /** Load the multi-table definition a duplicated card points at, so it can be forked. */
  const loadMultiTableConfig = async (ref: MultiTableRef): Promise<MultiTableConfig | undefined> => {
    const listed = dashboardMultiTable.multitables.find(m => m.id === ref.id)
    if (listed?.config) return listed.config
    try {
      const res = await axios.get<MultiTable>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables/${ref.id}`
      )
      if (res.data.config) return res.data.config
    } catch {
      // Not dashboard-owned (legacy dashboards point at job-owned multi-tables)
    }
    const refJob = ref.job || dashboardJob
    if (!refJob) return undefined
    const res = await axios.get<MultiTable>(`${API_BASE_URL}/multitables/${customer}/${refJob}/${ref.id}`)
    return res.data.config
  }

  /**
   * Fork every multi-table definition a pasted node references (#967).
   *
   * The copy gets its OWN multi-table row, pre-pointed at the same tables, so a
   * pasted column of analysis cards works immediately and the two cards can
   * later diverge. Table references carry over; everything keyed off the id —
   * edit history, analyses, subscribers — starts empty because the id is fresh.
   *
   * Returns old id -> new id. Ids missing from the map failed to fork; those
   * cards keep pointing at the original, which is degraded but not broken.
   */
  const forkMultiTables = async (refs: MultiTableRef[]): Promise<Record<string, string>> => {
    const mapping: Record<string, string> = {}
    if (refs.length === 0 || !dashboardId || dashboardId === 'new') return mapping
    let failed = 0
    for (const ref of refs) {
      try {
        const sourceConfig = await loadMultiTableConfig(ref)
        const tables = sourceConfig?.tables || []
        if (tables.length === 0) {
          failed++
          continue
        }
        const created = await dashboardMultiTable.createMultiTable({
          tables,
          name: sourceConfig?.name || 'Dashboard Analysis',
        })
        mapping[ref.id] = created.id
      } catch (err) {
        console.error('[Dashboard] Failed to fork multi-table on duplicate:', ref.id, err)
        failed++
      }
    }
    if (failed > 0) {
      snackbar.warning(
        failed === 1
          ? "Couldn't duplicate the analysis definition — the copy shares the original's tables until you give it its own."
          : `Couldn't duplicate ${failed} analysis definitions — those copies share the originals' tables until you give them their own.`
      )
    }
    return mapping
  }

  // The duplicate commits live in a hook: each one waits on a multi-table fork,
  // and everything it commits must be rebuilt from live state after that wait
  // (and no second duplicate may start meanwhile). See useDashboardDuplicate.
  const {
    isDuplicating,
    duplicateWidget: handleDuplicateWidget,
    duplicateInnerWidget: handleDuplicateInnerWidget,
    duplicateRow: handleDuplicateRow,
    duplicatePage: handleDuplicatePage,
  } = useDashboardDuplicate({
    getConfig: () => configRef.current,
    getActivePageIndex: () => activePageIndexRef.current,
    commitConfig: handleConfigChange,
    commitLayout: handleLayoutChange,
    forkMultiTables,
    onDropped: (reason) => snackbar.warning(reason),
    onPageDuplicated: setActivePageIndex,
  })

  // Saving a cell is several HTTP round-trips, so like the duplicate commits it
  // must rebuild from live state afterwards — see useDashboardCellSave.
  const { saveCell } = useDashboardCellSave({
    getConfig: () => configRef.current,
    getActivePageIndex: () => activePageIndexRef.current,
    // #1810: the first run snapshots the active page's filters.
    getPageJobFilters: (widgetJob) =>
      cardRunFilters((configRef.current.pages || [])[activePageIndexRef.current], widgetJob),
    commitLayout: handleLayoutChange,
    customer,
    job: dashboardJob,
    createMultiTable: dashboardMultiTable.createMultiTable,
    updateMultiTable: dashboardMultiTable.updateMultiTable,
    onDropped: (reason) => snackbar.warning(reason),
    // #1219 gate 2: the first-ever analysis start used to be swallowed to a
    // console.error, so a refusal (or any other failure) looked identical to
    // a clean save. Surface it — the card still committed, only the run didn't.
    onAnalysisStartFailed: (message) => snackbar.warning(message),
  })

  const handleSaveCell = async (cell: DashboardCell) => {
    if (!editingCell) return
    // 'failed' keeps the modal open so the user can retry the save — but it
    // must never fail silently (#1282): say so, or the Save button just looks
    // frozen.
    if (await saveCell(editingCell, cell) === 'failed') {
      snackbar.error("Couldn't save this card. Its tables could not be written — check the tables and try again.")
      return
    }
    setEditingCell(null)
  }

  // useVoiceRegistration snapshots uiTools once per pageKey, so a tool that
  // closed over this render's handlers would keep first-render state forever —
  // run_analysis_* would read isBusy===false and an empty layout for the rest
  // of the session. The tools therefore call through a ref that every render
  // refreshes. (The hook's own startRun re-checks isBusy as well, so no entry
  // path can bypass the double-run gate.)
  const openActivePagePalette = () => setPaletteTargetIndex(activePageIndexRef.current)
  const voiceActionsRef = useRef({ handleAddPage, handleAddRow, openSettings, handleRunPage, handleRunAll, openActivePagePalette, handleViewClick, handleEditCell, activeLayout })
  voiceActionsRef.current = { handleAddPage, handleAddRow, openSettings, handleRunPage, handleRunAll, openActivePagePalette, handleViewClick, handleEditCell, activeLayout }

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    // dashboardId is part of the key so switching dashboards re-registers the
    // tools rather than reusing the previous dashboard's snapshot — same shape
    // as framework-editor / autocoder-pipeline.
    // Page count in the key: go_to_page's options are snapshotted per key.
    pageKey: `dashboard-editor:${customer}:${dashboardJob}:${dashboardId}:${(config.pages || []).length}`,
    pageContext: () => `The dashboard editor for ${names.customer}. You can add pages and rows, open settings (sharing, about, history), open the current page's brand colours, and (when the user asks) regenerate AI insights — regenerating spends tokens. Editing individual cells is done on screen. ` +
      `Pages: ${(config.pages || []).map((p, i) => `${i + 1}. ${p.name || `Page ${i + 1}`}`).join('; ') || 'one page'}. On page ${activePageIndex + 1}.`,
    uiTools: buildVoiceTools({
      actions: [
        { name: 'add_page', label: 'add a page to the dashboard', run: () => voiceActionsRef.current.handleAddPage() },
        { name: 'add_row', label: 'add a row to the current page', run: () => voiceActionsRef.current.handleAddRow() },
        { name: 'open_dashboard_settings', label: 'open dashboard settings', run: () => voiceActionsRef.current.openSettings('about') },
        { name: 'open_brand_palette', label: "open the current page's brand colours", run: () => voiceActionsRef.current.openActivePagePalette() },
        { name: 'run_analysis_page', label: 'regenerate AI insights for this page', run: () => voiceActionsRef.current.handleRunPage(), costly: true, note: 'Tabulates every cell on the current page.' },
        { name: 'run_analysis_all', label: 'regenerate AI insights for the whole dashboard', run: () => voiceActionsRef.current.handleRunAll(), costly: true, note: 'Tabulates every cell on every page.' },
        // #1323: the rest of the toolbar and the widget editor.
        { name: 'preview_dashboard', label: 'preview the dashboard as a viewer sees it', run: () => voiceActionsRef.current.handleViewClick(), enabled: () => dashboardId !== 'new' },
        { name: 'edit_first_widget', label: "open the current page's first widget in the widget editor", run: () => voiceActionsRef.current.handleEditCell(0, 0), enabled: () => (voiceActionsRef.current.activeLayout.rows[0]?.cells.length ?? 0) > 0, note: 'The widget editor is where the table, the chart type and the columns are chosen.' },
      ],
      choices: [
        {
          name: 'go_to_page',
          label: 'the dashboard page to work on',
          options: (config.pages || []).map((p, i) => ({ value: String(i), describe: p.name || `Page ${i + 1}` })),
          optionsFrom: 'the page description (its numbered page list; the value is the number minus 1)',
          select: (v) => { setActivePageIndex(Number(v)); return `on page ${Number(v) + 1}` },
          enabled: () => (config.pages || []).length > 1,
        },
        {
          // The page menu opens on right-click only, which no page control
          // can reach (#1363). This opens it at the page's tab.
          name: 'open_page_menu',
          label: 'the page whose menu to open (rename, duplicate, move, delete)',
          options: (config.pages || []).map((p, i) => ({ value: String(i), describe: p.name || `Page ${i + 1}` })),
          optionsFrom: 'the page description (its numbered page list; the value is the number minus 1)',
          select: (v) => {
            const tab = document.getElementById(`dashboard-page-tab-${v}`)
            if (!tab) return 'that page tab is not on screen'
            setPageMenuAnchor(tab)
            setPageMenuIndex(Number(v))
            return `the menu for page ${Number(v) + 1} is open`
          },
        },
      ],
    }),
  })

  // Gate on auth state, not the raw query `isPending` — a disabled query (no
  // user yet, or a brand-new dashboard) reports isPending=true forever, which
  // otherwise spins the page indefinitely on direct-URL access with an expired
  // session. `dashboardId === 'new'` naturally resolves to 'ready' here.
  const loadState = resolveDashboardLoadState({
    initialized: account.initialized,
    hasUser: Boolean(account.user),
    dashboardIsLoading,
  })

  if (loadState === 'unauthenticated') {
    account.setPostLoginRoute({ name: 'dashboard-editor', params: { customer, dashboardId } })
    router.navigate('home')
  }

  if (loadState !== 'ready') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (dashboardIsError && dashboardId !== 'new') {
    const message = dashboardError instanceof Error ? dashboardError.message : 'Failed to load dashboard'
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{message}</Alert>
      </Box>
    )
  }

  const currentEditingCell = (() => {
    if (!editingCell) return null
    const outerCell = activeLayout.rows[editingCell.rowIndex]?.cells[editingCell.cellIndex]
    if (editingCell.innerRowIndex !== undefined && editingCell.innerCellIndex !== undefined) {
      // A report pillar addresses entry k as (k, 0) — the same address the
      // nested layout it replaced used (#1080).
      if (outerCell?.widget_type === 'report_pillar') {
        return outerCell.report_pillar_data?.entries[editingCell.innerRowIndex]?.cell || null
      }
      return outerCell?.layout_data?.rows[editingCell.innerRowIndex]?.cells[editingCell.innerCellIndex] || null
    }
    return outerCell || null
  })()

  return (
    // #1229 C4: every table picker in the editor (widget editors included —
    // their dialogs portal but keep this context) reads the job set here.
    // The Box is viewport-locked (height, not minHeight): the canvas owns
    // both scrollbars, so wide pages scroll sideways with the bar always in
    // reach instead of growing the document past the window edge.
    <DashboardJobsProvider value={dashboardJobsValue}>
    {/* Every widget's table load names this dashboard (the dashboard grant). */}
    <DashboardGrantContext.Provider value={dashboardId && dashboardId !== 'new' ? dashboardId : null}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', maxWidth: '100vw', overflow: 'hidden' }}>
      {/* Minimal header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'white',
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flexShrink: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            DataViz
          </Button>
          {/* #1183 (Matt, 2 Sep): the dashboard name sits inline after the
              back link — "← DataViz: <name>" — in the SAME font size,
              replacing the old centred h6 title. */}
          <Typography
            noWrap
            title={config.name || 'New Dashboard'}
            sx={{ fontSize: '0.8125rem', fontWeight: 500, minWidth: 0, ml: -1.75 }}
          >
            : {config.name || 'New Dashboard'}
          </Typography>
          {/* #1229 C3: the multi marker next to the name, the same words as
              the homepage chip, so it is obvious in edit, preview and view. */}
          {isMultiJob && (
            <Tooltip title={dashboardJobs.map(jobLabel).join(' · ')}>
              <Chip size="small" variant="outlined" label={`Multi: ${dashboardJobs.length} jobs`} sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
            </Tooltip>
          )}
          {updateIsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Saving...
              </Typography>
            </Box>
          )}
          {/* Clone-on-save (#962): a non-owner's edits are never auto-saved,
              so say so plainly and give them the one route that does save. */}
          {!canOverwrite && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {/* The caption is the compressible element in a tight header:
                  it truncates (full text in the tooltip) so the fork button
                  and the dashboard title keep their room. */}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title="You don't own this dashboard — changes aren't being saved."
                sx={{ minWidth: 0 }}
              >
                You don't own this dashboard — changes aren't being saved.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={handleSaveToMyDashboards}
                disabled={forkIsLoading}
                startIcon={forkIsLoading ? <CircularProgress size={14} /> : undefined}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                Save to My dashboards
              </Button>
            </Box>
          )}
        </Box>
        {/* The title moved inline after the back link (#1183, 2 Sep); this
            spacer keeps the right-hand action group pushed to the edge. */}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {bulkRun.run && (() => {
            const { total, completed, failed, refused, inFlight } = bulkRun.counts
            const cache = bulkRun.cacheProgress
            const warming = bulkRun.isWarmingCache
              ? cache && cache.total > 0
                ? ` (warming cache ${cache.cached}/${cache.total}…)`
                : ' (warming cache…)'
              : ''
            // #1219 gate 2: refused is reported apart from failed — "n not
            // permitted" instead of folding it into "n failed", since a
            // refusal is a permission decision, not a bug, and "Retry
            // failed" below never offers to re-post one.
            const settledLabel = [
              failed > 0 ? `${failed} failed` : null,
              refused > 0 ? `${refused} not permitted` : null,
            ].filter(Boolean).join(', ')
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mr: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {inFlight > 0 && <CircularProgress size={14} />}
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {inFlight > 0
                    ? `Regenerating ${bulkRun.run.label}: ${completed + failed + refused}/${total}${warming}`
                    : settledLabel
                      ? `${bulkRun.run.label}: ${settledLabel} of ${total}`
                      : `${bulkRun.run.label}: all ${total} done`}
                </Typography>
                {failed > 0 && (
                  <Button
                    size="small"
                    color="info"
                    onClick={handleRetryFailedBulkRun}
                    disabled={bulkRun.isStarting || !canRegenerate}
                    sx={{ textTransform: 'none' }}
                  >
                    Retry failed ({failed})
                  </Button>
                )}
                <IconButton size="small" onClick={bulkRun.dismiss} aria-label="Dismiss">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          })()}
          {/* Unified filtering (#1183, 2 Sep call): the separate "Filter all
              pages" toolbar button is gone — the ONE entry point is "Filter
              this page" in the page bar below, with an "Apply to all pages"
              toggle that promotes/demotes the expression. The dashboard-level
              filter still exists in storage; it just has no second control. */}
          {dashboardId !== 'new' && (
            <Tooltip
              describeChild
              title={
                !canOverwrite
                  ? 'You do not have permission to regenerate this dashboard.'
                  : !canRegenerate
                    ? aiAccess.refusalFor('genai_cards')
                    : bulkRun.isBusy
                    ? 'A regenerate run is still in progress'
                    : anyPageHasAnalysisWidgets ? '' : 'Add analysis widgets to the dashboard'
              }
            >
              <span>
                <Button
                  startIcon={bulkRun.isBusy ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setRegenMenuAnchor(e.currentTarget)}
                  variant="outlined"
                  color="info"
                  size="small"
                  disabled={!canOverwrite || !canRegenerate || bulkRun.isBusy || !anyPageHasAnalysisWidgets}
                  sx={{ textTransform: 'none' }}
                >
                  Regenerate AI Insights
                </Button>
              </span>
            </Tooltip>
          )}
          <Menu anchorEl={regenMenuAnchor} open={Boolean(regenMenuAnchor)} onClose={() => setRegenMenuAnchor(null)}>
            <MenuItem
              disabled={!canOverwrite || !canRegenerate || bulkRun.isBusy || !pageHasAnalysisWidgets}
              onClick={() => { setRegenMenuAnchor(null); handleRunPage() }}
            >
              This page
            </MenuItem>
            <MenuItem
              disabled={!canOverwrite || !canRegenerate || bulkRun.isBusy || !anyPageHasAnalysisWidgets}
              onClick={() => { setRegenMenuAnchor(null); handleRunAll() }}
            >
              Whole dashboard
            </MenuItem>
          </Menu>
          <PageAgentButton
            pageKey="dashboard-editor"
            customer={account.customer?.name || customer}
            open={helperOpen}
            onClick={() => {
              if (!helperOpen && insights.isOpen) insights.toggle()
              setHelperOpen(o => !o)
            }}
          />
          {dashboardId !== 'new' && (
            <Tooltip describeChild title={canInsights ? '' : aiAccess.refusalFor('dashboard_insights')}>
              <span>
                <Button
                  startIcon={<InsightsIcon />}
                  onClick={() => { if (!insights.isOpen) setHelperOpen(false); insights.toggle() }}
                  disabled={!canInsights}
                  size="small"
                  color="inherit"
                  sx={{ textTransform: 'none' }}
                >
                  Insights
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Settings">
            <IconButton onClick={() => openSettings('about')} size="small">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          {dashboardId !== 'new' && (
            <Tooltip title="Preview">
              <IconButton onClick={handleViewClick} size="small">
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>


      {/* Page tabs — ALWAYS shown (#1182): a new dashboard opens with Page 1
          already created, and every tab carries the palette icon that opens
          its Page brand colors panel. */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {pages.length > 0 && (
        <Tabs
          value={Math.min(activePageIndex, pages.length - 1)}
          onChange={(_e, newValue) => setActivePageIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
        >
          {pages.map((page, i) => (
            <Tab
              key={page.id}
              id={`dashboard-page-tab-${i}`}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {page.name}
                  <Tooltip title="Page theme">
                    {/* span, not IconButton: a Tab is already a button and
                        buttons cannot nest. */}
                    <Box
                      component="span"
                      role="button"
                      aria-label={`Theme for ${page.name}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setPaletteTargetIndex(i)
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: page.brand_palette_key ? 'info.main' : 'text.disabled',
                        '&:hover': { color: 'info.main' },
                      }}
                    >
                      <PaletteIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                </Box>
              }
              onContextMenu={(e) => {
                e.preventDefault()
                setPageMenuAnchor(e.currentTarget)
                setPageMenuIndex(i)
              }}
              onDoubleClick={() => {
                setRenamingPageIndex(i)
                setRenameValue(page.name)
              }}
            />
          ))}
        </Tabs>
        )}
        <Tooltip title="Add Page">
          <IconButton size="small" onClick={handleAddPage} sx={{ mx: 1 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filter bar (#1183; #1229 A1, 3 Sep call) — below the page tabs, and
          the ONLY filter authoring: "Filter this page" writes THIS page's
          filter. "Apply to all pages" is gone: an editor who wants a page
          filtered sets it on that page, every page, explicitly, so a page
          can never be blanked by a dashboard-wide filter it does not show.
          Blue is the edit colour (the viewer's quick filter is orange). On a
          multi-job dashboard the dialog carries the job selector and the
          bar shows one chip per filtered job, by display name. Centred
          under the page tabs (Matt, #1229 A2 — it sat hard left). */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', px: 2, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={filterDisabledReason || ''}>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="info"
              startIcon={<FilterListIcon />}
              onClick={() => { setFilterJob(''); setPageFilterOpen(true) }}
              disabled={Boolean(filterDisabledReason)}
              sx={{ textTransform: 'none' }}
            >
              Filter this page
            </Button>
          </span>
        </Tooltip>
        {isMultiJob ? (
          <>
            {/* Read the RESOLVED per-job filters — the same source the widget
                lines use — so a converted page's legacy string shows as a
                chip too, and bar and widgets can never disagree (#1240 A2). */}
            {dashboardJobs.filter((j) => editorStoredFilters[j]).map((j) => (
              <Chip key={`p-${j}`} size="small" color="info"
                label={`${jobLabel(j)}: ${editorStoredFilters[j]}`}
                onClick={() => { setFilterJob(j); setPageFilterOpen(true) }}
                onDelete={() => setJobPageFilter(j, '')}
                sx={{ maxWidth: 320 }} />
            ))}
            {dashboardJobs.every((j) => !editorStoredFilters[j]) && (
              <Chip size="small" variant="outlined" label="None" />
            )}
          </>
        ) : (
          <>
            {singleJobPageFilter ? (
              <Chip
                size="small"
                color="info"
                label={singleJobPageFilter}
                onClick={() => setPageFilterOpen(true)}
                onDelete={handleClearPageFilter}
                sx={{ maxWidth: 360 }}
              />
            ) : (
              <Chip size="small" variant="outlined" label="None" />
            )}
          </>
        )}
      </Box>

      {/* Page context menu */}
      <Menu
        anchorEl={pageMenuAnchor}
        open={Boolean(pageMenuAnchor)}
        onClose={() => { setPageMenuAnchor(null); setPageMenuIndex(null) }}
      >
        <MenuItem onClick={() => {
          if (pageMenuIndex !== null) {
            setRenamingPageIndex(pageMenuIndex)
            setRenameValue(pages[pageMenuIndex]?.name || '')
          }
          setPageMenuAnchor(null)
          setPageMenuIndex(null)
        }}>
          Rename
        </MenuItem>
        <MenuItem
          disabled={isDuplicating}
          onClick={() => {
            if (pageMenuIndex !== null) handleDuplicatePage(pageMenuIndex)
            setPageMenuAnchor(null)
            setPageMenuIndex(null)
          }}
        >
          {isDuplicating ? 'Duplicating…' : 'Duplicate Tab'}
        </MenuItem>
        {pageMenuIndex !== null && pageMenuIndex > 0 && (
          <MenuItem onClick={() => {
            handleMovePageLeft(pageMenuIndex!)
            setPageMenuAnchor(null)
            setPageMenuIndex(null)
          }}>
            Move Left
          </MenuItem>
        )}
        {pageMenuIndex !== null && pageMenuIndex < pages.length - 1 && (
          <MenuItem onClick={() => {
            handleMovePageRight(pageMenuIndex!)
            setPageMenuAnchor(null)
            setPageMenuIndex(null)
          }}>
            Move Right
          </MenuItem>
        )}
        {pages.length > 1 && (
          <MenuItem onClick={() => {
            if (pageMenuIndex !== null) setDeletePageIndex(pageMenuIndex)
            setPageMenuAnchor(null)
            setPageMenuIndex(null)
          }} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Rename page dialog */}
      <Dialog open={renamingPageIndex !== null} onClose={() => setRenamingPageIndex(null)}>
        <DialogTitle>Rename Page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renamingPageIndex !== null) {
                handleRenamePage(renamingPageIndex, renameValue)
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingPageIndex(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              if (renamingPageIndex !== null) handleRenamePage(renamingPageIndex, renameValue)
            }}
            disabled={!renameValue.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete page confirmation dialog */}
      <Dialog open={deletePageIndex !== null} onClose={() => setDeletePageIndex(null)}>
        <DialogTitle>Delete Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deletePageIndex !== null ? pages[deletePageIndex]?.name : ''}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePageIndex(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (deletePageIndex !== null) handleDeletePage(deletePageIndex)
              setDeletePageIndex(null)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dashboard editor canvas — WYSIWYG, with the widget palette down the
          side. Everything structural happens by dragging: a palette tile onto
          a seam or a gap, or a placed widget's grab handle onto another one. */}
      <DndContext
        sensors={dragSensors}
        collisionDetection={layoutCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0, minWidth: 0, backgroundColor: 'grey.50', position: 'relative' }}>
          {/* The palette OVERLAYS the canvas rather than consuming layout
              width: the canvas keeps its full width (and its horizontal
              scrollbar) whether the palette is open or folded to the rail. */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, zIndex: 6, boxShadow: 3 }}>
            <WidgetPalette
              isManager={!!account.user?.role_permissions?.isManager}
              subtitle="Drag onto the dashboard"
              collapsed={paletteCollapsed}
              onToggleCollapse={handleTogglePalette}
            />
          </Box>
          {/* minWidth 0: without it this flex child refuses to shrink below
              its content width, so a wide page pushes the whole document (top
              chrome included) past the window edge instead of scrolling here. */}
          <Box sx={{ flex: 1, minWidth: 0, p: 1.5, pl: paletteCollapsed ? '48px' : '252px', overflow: 'auto', transition: 'padding-left 150ms ease' }}>
            <Box
              key={activePage?.id || activePageIndex}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
                minHeight: 400,
                p: 1,
              }}
            >
              <LayoutDropZone
                id="editor-rowgap-0"
                orientation="row"
                target={{ kind: 'row-gap', index: 0 }}
                emphasize={activeLayout.rows.length === 0}
                emptyLabel="Drag a widget here to start building this page"
              />
              <FilterSignpostProvider value={signpostValue}>
              {activeLayout.rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <DashboardEditorRow
                    row={row}
                    rowIndex={rowIndex}
                    totalRows={activeLayout.rows.length}
                    customer={customer}
                    job={dashboardJob}
                    dashboardId={dashboardId}
                    canRegenerate={canOverwrite && canRegenerate}
                    globalFilter={resolvedPageFilter}
                    jobFilters={jobFilters}
                    colorMap={colorMap}
                    themeColorMaps={themeColorMaps}
                    onDeleteRow={() => handleDeleteRow(rowIndex)}
                    onEditCell={(cellIndex) => handleEditCell(rowIndex, cellIndex)}
                    onDeleteCell={(cellIndex) => handleDeleteCell(rowIndex, cellIndex)}
                    onRemoveTableReference={(cellIndex) => handleRemoveTableReference(rowIndex, cellIndex)}
                    onMoveRowUp={() => handleMoveRowUp(rowIndex)}
                    onMoveRowDown={() => handleMoveRowDown(rowIndex)}
                    onResizeBoundary={(boundaryIndex, deltaPct) => handleResizeBoundary(rowIndex, boundaryIndex, deltaPct)}
                    onDistributeEvenly={() => handleDistributeEvenly(rowIndex)}
                    onInsertInnerRow={(cellIndex, afterInnerRowIndex) => handleInsertInnerRow(rowIndex, cellIndex, afterInnerRowIndex)}
                    onEditInnerCell={(cellIndex, innerRowIndex, innerCellIndex) => handleEditInnerCell(rowIndex, cellIndex, innerRowIndex, innerCellIndex)}
                    onDeleteInnerRow={(cellIndex, innerRowIndex) => handleDeleteInnerRow(rowIndex, cellIndex, innerRowIndex)}
                    onMoveInnerRow={(cellIndex, innerRowIndex, direction) => handleMoveInnerRow(rowIndex, cellIndex, innerRowIndex, direction)}
                    onUpdateInnerCellData={(cellIndex, innerRowIndex, innerCellIndex, updatedCell) => handleUpdateInnerCellData(rowIndex, cellIndex, innerRowIndex, innerCellIndex, updatedCell)}
                    duplicateBusy={isDuplicating}
                    onDuplicateCell={(cellIndex) => handleDuplicateWidget({ rowIndex, cellIndex })}
                    onDuplicateInnerCell={(cellIndex, innerRowIndex, innerCellIndex) => handleDuplicateInnerWidget(rowIndex, cellIndex, innerRowIndex, innerCellIndex)}
                    onDuplicateRow={() => handleDuplicateRow(rowIndex)}
                    activeDrag={activeDrag}
                  />
                  <LayoutDropZone
                    id={`editor-rowgap-${rowIndex + 1}`}
                    orientation="row"
                    target={{ kind: 'row-gap', index: rowIndex + 1 }}
                  />
                </React.Fragment>
              ))}
              </FilterSignpostProvider>
            </Box>
          </Box>
        </Box>
        <DragOverlay>
          {(() => {
            // #1111: canvas moves get a ghost too, not just palette drags —
            // the same card, looked up from the dragged cell's widget type.
            if (activeDrag?.source === 'palette' && WIDGET_TYPE_OPTIONS[activeDrag.widgetType]) {
              return <WidgetPaletteDragGhost widget={WIDGET_TYPE_OPTIONS[activeDrag.widgetType]} />
            }
            if (activeDrag?.source === 'canvas') {
              // #1129: a canvas move ghosts as a titled card — the widget's
              // own title with its type icon (WidgetDragGhost resolves both
              // through dragGhostOption/dragGhostTitle, so charts and tables
              // — widget_type 'table_graph' — always get a ghost, #1130).
              const dragged = activeLayout.rows[activeDrag.ref.rowIndex]?.cells[activeDrag.ref.cellIndex]
              if (dragged) return <WidgetDragGhost cell={dragged} />
            }
            return null
          })()}
        </DragOverlay>
      </DndContext>

      {/* Settings Modal */}
      <DashboardSettingsModal
        open={settingsOpen}
        initialTab={settingsTab}
        name={config.name}
        description={config.description}
        objectives={config.objectives || ''}
        keyMetrics={config.key_metrics || ''}
        pages={config.pages || []}
        onSave={handleSettingsSave}
        onClose={() => {
          // If new dashboard with no name, go back
          if (dashboardId === 'new' && !config.name) {
            handleBackClick()
          } else {
            setSettingsOpen(false)
          }
        }}
        isLoading={updateIsLoading}
        customer={customer}
        job={dashboardJob}
        customerDisplayName={customerDisplayName}
        jobDisplayName={jobDisplayName}
        customerContext={clientJobContext.customerContext}
        jobContext={clientJobContext.jobContext}
        onOpenCustomer={() => router.navigate('admin-context', { customer })}
        onOpenJob={() => router.navigate('admin-context', { customer })}
        jobs={dashboardId !== 'new' && dashboardJobs.length > 0 ? dashboardJobs : undefined}
        primaryJob={primaryJob}
        availableJobs={sessionCust?.sessionJobs?.filter((j) => j.name).map((j) => ({ name: j.name as string, displayName: j.displayName || undefined }))}
        referencedJobs={collectConfigJobCounts(config)}
        onAddJob={async (j) => { await addDashboardJob(dashboardId, j) }}
        onRemoveJob={async (j) => { await removeDashboardJob(dashboardId, j) }}
        jobMutationLoading={jobMutationIsLoading}
        dashboardId={dashboardId}
        shareId={dashboard?.share_id}
        shareAccessLevel={dashboard?.share_access_level}
        shareEnabled={dashboard?.share_enabled}
        onShare={async (accessLevel, password) => {
          await shareDashboard(dashboardId, accessLevel, password)
        }}
        onDisableShare={async () => {
          await disableShare(dashboardId)
        }}
        shareIsLoading={shareIsLoading}
        currentSignOff={config.sign_off}
        signOffRevoked={config.sign_off_revoked}
        widgetTitles={widgetTitles}
        onSaveAsTemplate={dashboardId !== 'new' ? () => setTemplateDialogOpen(true) : undefined}
      />

      <SaveAsTemplateDialog
        open={templateDialogOpen}
        defaultName={config.name}
        onClose={() => setTemplateDialogOpen(false)}
        onSave={handleSaveAsTemplate}
        isLoading={createTemplateIsLoading}
      />

      {/* Page brand colors (#1182) — opened from a page tab's palette icon.
          Selecting a theme stores its id on the page (live reference). */}
      {paletteTargetIndex !== null && pages[paletteTargetIndex] && (
        <PageBrandColorsPanel
          open
          onClose={() => setPaletteTargetIndex(null)}
          pageName={pages[paletteTargetIndex].name}
          themeId={pages[paletteTargetIndex].brand_palette_key}
          onSelectTheme={(themeId) => handleSelectPageTheme(paletteTargetIndex, themeId)}
          themes={brandThemes.themes}
          themesLoading={brandThemes.isLoading}
          jobPalette={brandPalette.palette || config.brand_palette}
          canManageThemes
          onSaveTheme={async (id, name, palette) => { await brandThemes.updateTheme(id, name, palette) }}
          onCreateTheme={async (name, palette) => { await brandThemes.createTheme(name, palette) }}
          onDeleteTheme={brandThemes.deleteTheme}
          currentUserEmail={account.user?.session_info?.email || ''}
          canPromoteThemes={canPromoteDashboard(account.user?.role_permissions)}
          onPromoteTheme={brandThemes.promoteTheme}
          onDemoteTheme={brandThemes.demoteTheme}
          onLoadThemeHistory={brandThemes.loadThemeHistory}
          whoToAsk={whoToAsk}
          onResetCharts={() => handleResetPageCharts(paletteTargetIndex)}
        />
      )}

      {/* Page filter dialog (#1183) — this page's filter. On a multi-job
          dashboard the job selector is inside the dialog (#1229 C4): the
          expression is authored against the picked job's variable tree and
          saved into that job's page.filters entry. The dashboard dialog
          never shows the Survey/AutoCoder source selector (#1229 A6). */}
      <FilterEditorModal
        open={pageFilterOpen}
        onClose={() => setPageFilterOpen(false)}
        onSave={handlePageFilterSave}
        currentFilter={singleJobPageFilter}
        customer={customer}
        job={isMultiJob ? (filterJob || undefined) : (dashboardJob || (isSingleJob ? [...tableGraphJobs][0] : undefined))}
        jobs={isMultiJob ? dashboardJobsValue.jobs : undefined}
        filtersByJob={isMultiJob ? editorStoredFilters : undefined}
        onSaveForJob={(j, expr) => { setJobPageFilter(j, expr); setPageFilterOpen(false) }}
        hideSourceSelector
      />

      <PageAgentDrawer
        pageKey="dashboard-editor"
        customer={account.customer?.name || customer}
        job={dashboardJob}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />

      {/* Insights Chat Drawer (#974) */}
      <ChatDrawer
        open={insights.isOpen}
        onClose={insights.toggle}
        // The initial response is [system, user, assistant] — only the
        // assistant's summary onward is meant to render (mirrors Tables.tsx's
        // chat.messages.slice(2)).
        messages={insights.messages.slice(2)}
        onSendMessage={insights.send}
        isLoading={insights.isLoading}
        error={insights.error}
        scopeLabel={insights.boundScope?.pageName}
        framing={insightsSourceLines(insights.boundScope?.sources || [])}
        placeholder={
          insights.boundScope?.pageName
            ? `Ask about "${insights.boundScope.pageName}"...`
            : 'Ask about this dashboard...'
        }
        showAdvancedMode={false}
        personas={insightsPersonas}
        selectedPersona={insights.selectedPersona}
        onPersonaChange={insights.setSelectedPersona}
        inputHelper={
          // Context on-ramp (#974 ask 3): the helper text IS the link,
          // straight to Settings -> About.
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => openSettings('about')}
            sx={{ textAlign: 'left' }}
          >
            Tell us what this dashboard is about so we give better insights
          </Link>
        }
      />

      {/* Cell Edit Modal */}
      <CellEditModal
        open={Boolean(editingCell)}
        cell={currentEditingCell}
        customer={customer}
        job={dashboardJob}
        dashboardId={dashboardId}
        isManager={!!account.user?.role_permissions?.isManager}
        dashboardConfig={config}
        currentPageIndex={activePageIndex}
        themes={brandThemes.themes}
        pageThemeName={pageThemeName}
        colorMap={colorMap}
        themeColorMaps={themeColorMaps}
        // #1810: a new card's preview run snapshots the active page's filters.
        jobFilters={cardRunFilters(activePage, dashboardJob)}
        onSave={handleSaveCell}
        onClose={() => setEditingCell(null)}
      />

      {/* Confirm overwrite of manually-edited analysis text */}
      <Dialog open={!!overwriteConfirm} onClose={() => setOverwriteConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {(overwriteConfirm?.edited.length ?? 0) > 0
            ? 'Overwrite manual edits?'
            : `Regenerate AI insights — ${overwriteConfirm?.label}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Each analysis runs against the current and previous month derived
            from its table&apos;s most recent data columns. Tables whose data lags
            the report month are flagged in the generated text, and each widget
            shows which month its data runs to.
          </DialogContentText>
          {(overwriteConfirm?.edited.length ?? 0) > 0 && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {overwriteConfirm?.edited.length === 1
                  ? '1 analysis box has manual edits that will be overwritten:'
                  : `${overwriteConfirm?.edited.length ?? 0} analysis boxes have manual edits that will be overwritten:`}
              </DialogContentText>
              <Box component="ul" sx={{ pl: 3, mb: 2, maxHeight: 200, overflow: 'auto' }}>
                {overwriteConfirm?.edited.map((w, i) => (
                  <li key={`${w.multi_table_id}-${i}`}>
                    <Typography variant="body2">{w.title || w.multi_table_id || '(untitled)'}</Typography>
                  </li>
                ))}
              </Box>
              <DialogContentText>
                Regenerating {overwriteConfirm?.label} will replace these with freshly generated text. Continue?
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverwriteConfirm(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmOverwrite}
            color={(overwriteConfirm?.edited.length ?? 0) > 0 ? 'warning' : 'info'}
            variant="contained"
          >
            {(overwriteConfirm?.edited.length ?? 0) > 0
              ? 'Overwrite & regenerate'
              : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
    </DashboardGrantContext.Provider>
    </DashboardJobsProvider>
  )
}

export default DashboardEditor
